import { ProductDataType, ProductType } from '@pages/product-analyzer/Product';
import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import { getFeatureValueByPGAndCountry } from '@services/FeatureValue.service';
import { Option } from '@utils/pageConfig/Types';
import { Periodicity } from '@components/time-period-selector/PeriodsTypes';
import { PagesConfig } from '@utils/pageConfig/PagesConfigContext';
import { isMultiPgCommonFeatureAllowed } from '@utils/filterTrayUtils/FilterTrayUtils';

export interface FeatureValueQueryKey {
  countries: Option[];
  productGroups: Option[] | undefined;
  periodicity: Periodicity;
  hasSelectedCountry: boolean;
}

export interface FeatureValueQueryParams {
  productType: ProductType;
  selectedProductGroup: Option[] | undefined;
  selectedCountries: Option[];
  pagesConfig: PagesConfig;
  timeFrame: Periodicity;
  currentTotalName: string;
}

/**
 * Custom React hook to fetch feature values by product group and country using React Query.
 *
 * This hook builds a unique query key from the provided country, product group, and periodicity options,
 * ensuring proper cache separation for different filter combinations. It calls the `getFeatureValueByPGAndCountry`
 * service with the provided parameters and returns the React Query result, including data, loading, and error states.
 *
 * @param {FeatureValueQueryKey} keyParams - Parameters used to construct the query key for React Query caching.
 *   - `countries`: Array of country options to filter results.
 *   - `productGroups`: Array of product group options (optional).
 *   - `periodicity`: Time period for the query.
 *   - `hasSelectedCountry`: Boolean indicating if a country is selected.
 * @param {FeatureValueQueryParams} queryParams - Parameters passed to the API service function.
 *   - `productType`: Type of product being analyzed.
 *   - `selectedProductGroup`: Selected product groups (optional).
 *   - `selectedCountries`: Selected countries.
 *   - `pagesConfig`: Page configuration context.
 *   - `timeFrame`: Time frame for the query.
 *   - `currentTotalName`: Name of the current total to fetch.
 * @param {Omit<UseQueryOptions<any, any, any, any>, 'queryKey' | 'queryFn'>} [options] - Optional React Query options to customize query behavior (e.g., enabled, staleTime, refetchOnWindowFocus).
 *
 * @returns React Query result object containing:
 *   - `data`: The fetched feature values.
 *   - `isLoading`: Loading state.
 *   - `error`: Error state if the query fails.
 *   - Other React Query result properties.
 *
 * @example
 * const { data, isLoading } = useFeatureValueByPGAndCountry(keyParams, queryParams, { enabled: true });
 */
const useFeatureValueByPGAndCountry = (
  keyParams: FeatureValueQueryKey,
  queryParams: FeatureValueQueryParams,
  options?: Omit<UseQueryOptions<any, any, any, any>, 'queryKey' | 'queryFn'>,
) => {
  const { countries, productGroups, periodicity, hasSelectedCountry } = keyParams;
  const { pagesConfig, productType, selectedCountries, selectedProductGroup, timeFrame, currentTotalName } =
    queryParams;

  const includeCommonFeaturesOnly = isMultiPgCommonFeatureAllowed(pagesConfig.pageType);

  return useQuery({
    queryKey: [
      'feature-values',
      {
        countries: countries.map((country) => country.value),
        productGroups: productGroups?.map((pg) => pg.value),
        periodicity,
        hasSelectedCountry,
      },
    ],
    queryFn: ({ signal }) => {
      return getFeatureValueByPGAndCountry({
        clientId: pagesConfig.clientId,
        productType,
        productDataType: ProductDataType.PerformanceAnalyzer,
        country: selectedCountries.map((country) => country.value),
        periodType: timeFrame,
        productGroups: selectedProductGroup?.[0] ? selectedProductGroup.map(({ value }) => value) : [],
        currentTotalName,
        signal,
        includeCommonFeaturesOnly,
      });
    },
    refetchOnWindowFocus: false,
    ...options,
  });
};

export default useFeatureValueByPGAndCountry;
